/**
 * previewSync — Section Navigation UX. The Manual Builder's sidebar and its Live Preview
 * are two separate documents (the preview renders in an iframe), so "click a section in
 * the sidebar → scroll the preview to it" has to cross that boundary via `postMessage`.
 * This module owns the two small DOM primitives that make that safe and reusable:
 *
 *   - `scrollToSection` — runs INSIDE the preview document, smooth-scrolls to a section by
 *     its existing, stable schema id (the same id `SectionRenderer` already sets as every
 *     section's root DOM id — no new ids, no fragile querying).
 *   - `observeActiveSection` — runs INSIDE the preview document, reports back whichever
 *     section is currently in view as the user scrolls manually, so the sidebar can keep
 *     its highlight in sync without any click at all.
 *
 * The two message type strings are exported so the preview (message receiver/sender) and
 * the editor shell (message sender/receiver, in pages/builder) agree on the same contract
 * without duplicating string literals.
 */

/** parent → preview: "scroll to this section" */
export const SB_SCROLL_TO_SECTION = 'sb-scroll-to-section'
/** preview → parent: "this section is now the one in view" */
export const SB_ACTIVE_SECTION = 'sb-preview-active-section'

/**
 * Smooth-scrolls the given section into view, offset below the live site's own sticky
 * navbar (if one is rendered) so the section's heading is never hidden behind it. A no-op
 * if the id doesn't exist in the current document (e.g. a stale message after the section
 * was hidden/removed).
 */
export function scrollToSection(sectionId: string): void {
  const target = document.getElementById(sectionId)
  if (!target) return
  const navbar = document.querySelector('.sbp-navbar')
  const offset = (navbar?.getBoundingClientRect().height ?? 0) + 12
  const top = target.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
}

/**
 * Observes the given (ordered) section ids and calls `onActiveChange` with whichever one is
 * currently crossing a thin horizontal band near the top of the viewport — the standard
 * "scrollspy" technique, robust to sections of any height (a short section and a very tall
 * one are both picked up the same way, unlike a plain max-intersection-ratio approach).
 *
 * Only ids that currently resolve to a rendered element are observed, so this is always
 * safe to call fresh — after a drag-and-drop reorder, after a section is shown/hidden, or
 * after any content edit — as long as the caller re-invokes it (and disposes the previous
 * observer) whenever the id list changes. Returns the disposer.
 */
export function observeActiveSection(sectionIds: readonly string[], onActiveChange: (sectionId: string) => void): () => void {
  const elements = sectionIds.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el)
  if (elements.length === 0) return () => {}

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting)
      if (visible.length === 0) return
      const topmost = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b))
      onActiveChange(topmost.target.id)
    },
    { threshold: 0, rootMargin: '-35% 0px -55% 0px' },
  )
  elements.forEach((el) => observer.observe(el))
  return () => observer.disconnect()
}
